import type { EssentialPreferences, LoyaltyPreferences } from '@/types/profile';
import type {
  FlightSearchParams,
  HotelSearchParams,
  Flight,
  Hotel,
  PreferenceMatch,
  FlightAnalysisResponse,
  FlightSearchParamsForAnalysis,
  FlightResultsForAnalysis,
} from '@/types/travel';
import { supabase } from './supabase';
import { DuffelClient } from './duffel-client';
import { format } from 'date-fns';
import { apiFetch } from './api';

// Unused or unreferenced check for delete
export function calculateFlightMatch(
  flight: Flight,
  essentialPrefs?: EssentialPreferences | null,
  loyaltyPrefs?: LoyaltyPreferences | null
): PreferenceMatch {
  let totalScore = 0;
  let maxScore = 0;
  const matches: string[] = [];

  if (essentialPrefs?.seating) {
    maxScore += 40;
    if (flight.cabinClass === essentialPrefs.seating.value) {
      totalScore += 40;
      matches.push('Preferred cabin class');
    }
  }

  if (loyaltyPrefs?.airline?.program) {
    maxScore += 60;
    if (flight.airline.includes(loyaltyPrefs.airline.program)) {
      totalScore += 60;
      matches.push('Preferred airline');
    }
  }

  return {
    score: maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0,
    matches,
  };
}

// Unused or unreferenced check for delete
export async function getActiveConfig() {
  const { data, error } = await supabase
    .from('travel_api_configs')
    .select('*')
    .eq('is_active', true)
    .eq('type', 'search')
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data || {
    url: 'https://api.example.com/v1',
    api_key: 'default-key',
  };
}

// Unused or unreferenced check for delete
export async function getActiveItineraryConfig() {
  const { data, error } = await supabase
    .from('travel_api_configs')
    .select('*')
    .eq('is_active', true)
    .eq('type', 'itinerary')
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data || {
    url: 'https://api.example.com/v1',
    api_key: 'default-key',
  };
}

// Define a type for the search response
interface SearchResponseType<T> {
  data: T[];
}

// Unused or unreferenced check for delete
export class TravelAPI {
  private static async request<T>(endpoint: string, params: Record<string, any>): Promise<T> {
    try {
      if (endpoint.includes('/flights/search')) {
        return {
          data: [
            {
              id: '1',
              airline: 'Mock Airlines',
              flightNumber: 'MA123',
              origin: params.origin,
              destination: params.destination,
              departureTime: params.departureDate,
              arrivalTime: new Date(new Date(params.departureDate).getTime() + 3600000).toISOString(),
              price: 299.99,
              duration: '2h 30m',
              cabinClass: params.cabinClass || 'economy',
              stops: 0
            }
          ]
        } as T;
      }

      if (endpoint.includes('/hotels/search')) {
        return {
          data: [
            {
              id: '1',
              name: 'Mock Hotel',
              location: params.location,
              price: 199.99,
              pricePerNight: 199.99,
              rating: 4.5,
              amenities: ['WiFi', 'Pool', 'Spa'],
              description: 'A lovely mock hotel in the heart of the city',
              checkIn: params.checkIn,
              checkOut: params.checkOut,
              starRating: 4
            }
          ]
        } as T;
      }

      return { data: [] } as T;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  static async searchFlights(params: FlightSearchParams): Promise<SearchResponseType<Flight>> {
    try {
      if (!params.origin || !params.destination) {
        throw new Error('Origin and destination are required');
      }

      const cabinClass = params.cabinClass || 'economy';
      
      // Try Duffel API first
      try {
        const flights = await DuffelClient.searchFlights(params);
        return { data: flights };
      } catch (duffelError) {
        console.warn('Duffel API error, falling back to mock data:', duffelError);
        // Fallback to mock data
        return await this.request<SearchResponseType<Flight>>('/flights/search', {
          origin: params.origin,
          destination: params.destination,
          departureDate: format(
            typeof params.departureDate === 'string' 
              ? new Date(params.departureDate) 
              : params.departureDate || new Date(),
            "yyyy-MM-dd'T'HH:mm:ss'Z'"
          ),
          returnDate: typeof params.returnDate === 'string' ? params.returnDate : params.returnDate?.toISOString(),
          passengers: params.passengers || { adults: 1, children: 0 },
          cabinClass,
        });
      }
    } catch (error) {
      console.error('Error searching flights:', error);
      throw error;
    }
  }

  static async searchHotels(params: HotelSearchParams): Promise<SearchResponseType<Hotel>> {
    try {
      if (!params.location) {
        throw new Error('Location is required');
      }

      return await this.request<SearchResponseType<Hotel>>('/hotels/search', {
        location: params.location,
        checkIn: typeof params.checkIn === 'string' ? params.checkIn : params.checkIn?.toISOString(),
        checkOut: typeof params.checkOut === 'string' ? params.checkOut : params.checkOut?.toISOString(),
        guests: params.guests || 2,
        rooms: params.rooms || 1,
        starRating: params.starRating || [3, 4, 5],
      });
    } catch (error) {
      console.error('Error searching hotels:', error);
      throw error;
    }
  }

  static async getFlightDetails(flightId: string): Promise<Flight> {
    return this.request<Flight>(`/flights/${flightId}`, {});
  }

  static async getHotelDetails(hotelId: string): Promise<Hotel> {
    return this.request<Hotel>(`/hotels/${hotelId}`, {});
  }
}

/**
 * VOY-18/VOY-187: Generate flight analysis and booking recommendations
 * Calls the backend API to analyze flight search results and provide actionable advice
 * 
 * @param searchParams - Flight search parameters (origin, destination, dates, passengers, tripType)
 * @param flightResults - Optional flight search results for analysis
 * @returns FlightAnalysisResponse with structured analysis or error
 */
export async function generateFlightAnalysis(
  searchParams: FlightSearchParamsForAnalysis,
  flightResults?: FlightResultsForAnalysis
): Promise<FlightAnalysisResponse> {
  try {
    console.log('[FlightAnalysis] Generating analysis for:', {
      origin: searchParams.origin,
      destination: searchParams.destination,
      departureDate: searchParams.departureDate,
    });

    // Prepare flight results summary for the API
    const preparedFlightResults: FlightResultsForAnalysis = flightResults || {};
    
    // If we have offers, calculate price statistics
    if (flightResults?.offers && flightResults.offers.length > 0) {
      const prices = flightResults.offers.map((offer) => {
        // Handle both Flight and MultiSliceFlight types
        if ('totalPrice' in offer) {
          return offer.totalPrice;
        }
        return offer.price;
      }).filter((p): p is number => typeof p === 'number' && p > 0);
      
      if (prices.length > 0) {
        preparedFlightResults.prices = {
          min: Math.min(...prices),
          max: Math.max(...prices),
          average: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
        };
      }
      
      // Extract unique airlines
      const airlines = new Set<string>();
      flightResults.offers.forEach((offer) => {
        if ('airline' in offer && offer.airline) {
          airlines.add(offer.airline);
        }
        if ('slices' in offer) {
          offer.slices.forEach((slice) => {
            if (slice.airline) {
              airlines.add(slice.airline);
            }
          });
        }
      });
      preparedFlightResults.airlines = Array.from(airlines);
    }

    const response = await apiFetch('/api/itinerary/flight-analysis', {
      method: 'POST',
      body: JSON.stringify({
        searchParams,
        flightResults: preparedFlightResults,
      }),
      abortKey: `flight-analysis-${searchParams.origin}-${searchParams.destination}`,
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[FlightAnalysis] API error:', errorData);
      return {
        type: 'error',
        error: errorData.error || 'Failed to generate flight analysis',
        message: errorData.message,
      };
    }

    const analysisResponse: FlightAnalysisResponse = await response.json();
    
    console.log('[FlightAnalysis] Analysis received:', {
      type: analysisResponse.type,
      recommendation: analysisResponse.data?.recommendation?.action,
    });

    return analysisResponse;
  } catch (error) {
    console.error('[FlightAnalysis] Error:', error);
    return {
      type: 'error',
      error: 'Failed to generate flight analysis',
      message: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}